import React, { useCallback, useContext, useEffect, useMemo, useState } from "react"
import { Children } from "../../../../../../../../../components/miscellianous/children"
import { BSPatio } from "../../../../../../../../../core/dto/beem-shot/BSPatio/BSPatio"
import { BSPatioForm } from "../../../../../../../../../core/dto/beem-shot/BSPatio/BSPatioForm"
import { useBSPatio } from "../../../../../../../../../core/hooks/beem-shot/useBSPatio"
import { BSBuildingContext } from "../../../components/context/BSBuildingContext"
import { CalculStatusEnum } from "../../../../../../../../../core/enum/calculStatusEnum"
import { SuccessContext } from "../../../../../../../../../components/layout/success-snackbar"
import { ErrorContext } from "../../../../../../../../../components/layout/error-snackbar"

export const BSPatioContext = React.createContext<BSPatioStore>({} as BSPatioStore)

export function BSPatioContextProvider({ children }: Readonly<Children>): React.JSX.Element {
  const { fetchBSPatioList, createOrUpdateBSPatioList } = useBSPatio()
  const openSuccessSnackbar = useContext(SuccessContext)
  const openErrorSnackbar = useContext(ErrorContext)

  const { selectedBSBuilding, updateCalculStatusState } = useContext(BSBuildingContext)

  const [bsPatioList, setBSPatioList] = useState<BSPatio[]>([])
  const [isEditing, setIsEditing] = useState<boolean>(false)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)

  useEffect(() => {
    if (selectedBSBuilding?.id) {
      setIsLoading(true)
      fetchBSPatioList(selectedBSBuilding.id).then((newBSPatioList) => {
        setBSPatioList(newBSPatioList)
        setIsLoading(false)
      })
    }
  }, [fetchBSPatioList, selectedBSBuilding])

  const submitBSPatioDtoList: (newList: BSPatio[]) => Promise<any> = useCallback(
    (newList) => {
      if (!selectedBSBuilding?.id) {
        openErrorSnackbar(new Error("Il n'y a pas de bâtiment selectionné"))
        return Promise.resolve()
      }
      setIsSubmitting(true)
      return createOrUpdateBSPatioList(selectedBSBuilding.id, newList)
        .then((newBSPatioList) => {
          setBSPatioList(newBSPatioList)
          updateCalculStatusState(CalculStatusEnum.WAITING_FOR_CONFIGURATION)
          openSuccessSnackbar("Les patios ont bien été modifiés.")
          return newBSPatioList
        })
        .finally(() => {
          setIsSubmitting(false)
        })
    },
    [createOrUpdateBSPatioList, openErrorSnackbar, openSuccessSnackbar, selectedBSBuilding?.id, updateCalculStatusState]
  )

  const deleteBSPatioInBSPatioList: (patioIndex: number) => Promise<any> = useCallback(
    (patioIndex) => {
      const newPatioList = bsPatioList.filter((_, index) => index !== patioIndex)
      return submitBSPatioDtoList(newPatioList)
    },
    [bsPatioList, submitBSPatioDtoList]
  )

  const bsPatioStore = useMemo(
    () => ({
      bsPatioList,
      isEditing,
      isSubmitting,
      isLoading,
      setIsSubmitting,
      setIsEditing,
      setBSPatioList,
      deleteBSPatioInBSPatioList,
      submitBSPatioDtoList,
    }),
    [
      bsPatioList,
      isEditing,
      isSubmitting,
      isLoading,
      setIsSubmitting,
      setIsEditing,
      setBSPatioList,
      deleteBSPatioInBSPatioList,
      submitBSPatioDtoList,
    ]
  )
  return <BSPatioContext.Provider value={bsPatioStore}>{children}</BSPatioContext.Provider>
}

export interface BSPatioStore {
  bsPatioList: BSPatio[]
  isEditing: boolean
  isSubmitting: boolean
  isLoading: boolean
  setIsSubmitting: React.Dispatch<React.SetStateAction<boolean>>
  setIsEditing: React.Dispatch<React.SetStateAction<boolean>>
  setBSPatioList: React.Dispatch<React.SetStateAction<BSPatioForm[]>>
  deleteBSPatioInBSPatioList(patioIndex: number): Promise<any>
  submitBSPatioDtoList(newList: BSPatio[]): Promise<any>
}
